<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useDisplay } from 'vuetify'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import ImportHistoryModal from '@/modules/import/components/ImportHistoryModal.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import FileAttach from '@/modules/files/components/FileAttach.vue'
import FileList from '@/modules/files/components/FileList.vue'
import type { IExistingFile } from '@/modules/files/types/IExistingFile'
import HospitalStructurePanel from '@/modules/settings/components/HospitalStructurePanel.vue'
import ReportBrandingPanel from '@/modules/settings/components/ReportBrandingPanel.vue'
import WebhooksPanel from '@/modules/settings/components/WebhooksPanel.vue'
import { ERouteNames } from '@/router/ERouteNames'
import { useOrganizationSettingsStore } from '@/modules/settings/stores/organizationSettingsStore'
import type { OrganizationSettings } from '@/modules/settings/types/organizationSettings.types'
import { toMediaUrl } from '@/modules/settings/utils/toMediaUrl'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { SETTINGS_ANCHORS, SETTINGS_CONTEXTS } from '@/modules/onboarding/scenarios/settings/anchors'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'

type SettingsTab = 'organization' | 'structure' | 'branding' | 'integrations'

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
const LOGO_NAME = /\.(png|jpe?g|webp)$/i

const organizationSettingsStore = useOrganizationSettingsStore()
const feedbackStore = useFeedbackStore()
const permissionsStore = usePermissionsStore()
const { smAndDown } = useDisplay()
const router = useRouter()
const route = useRoute()
const historyOpen = ref(false)
const historyImportType = ref<'qualitycontrol' | 'locations'>('qualitycontrol')

function openQualityImport() {
  router.push({
    name: ERouteNames.IMPORT_DATA,
    params: { importType: 'qualitycontrol' },
    query: { from: route.fullPath },
  })
}

function openQualityHistory() {
  historyImportType.value = 'qualitycontrol'
  historyOpen.value = true
}

function openLocationsImport() {
  router.push({
    name: ERouteNames.IMPORT_DATA,
    params: { importType: 'locations' },
    query: { from: route.fullPath },
  })
}

function openLocationsHistory() {
  historyImportType.value = 'locations'
  historyOpen.value = true
}

const canEditOrganization = computed(() => !!permissionsStore.modules.hospital?.settings_edit)
const canEditStructure = computed(() => !!permissionsStore.modules.hospital?.structure_edit)
const canManageIntegrations = computed(() => !!permissionsStore.modules.integrations?.manage)
const canImportNorms = computed(() => !!permissionsStore.modules.admin?.admin_panel)

const tab = ref<SettingsTab>(
  !canEditOrganization.value && canEditStructure.value
    ? 'structure'
    : canEditOrganization.value ? 'organization' : canManageIntegrations.value ? 'integrations' : 'organization',
)
const loading = ref(false)
const saving = ref(false)
const error = ref('')
const logoFile = ref<File | null>(null)
const logoPreview = ref('')
const logoCleared = ref(false)
const logoInputRef = ref<HTMLInputElement | null>(null)
const isDragOver = ref(false)
const dragCounter = ref(0)
const deletingLogo = ref(false)

const pdAgreementFiles = ref<File[]>([])
const pdAgreementExistingUrl = ref('')
const pdAgreementAttachRef = ref<InstanceType<typeof FileAttach> | null>(null)
const pdAgreementSynced = ref(false)
const uploadingPdAgreement = ref(false)
const showPdAgreementDeleteConfirm = ref(false)

const pdConsentUrl = ref('')
const pdConsentSynced = ref(false)

/* Реквизиты, из которых бек собирает согласие на обработку ПД; проверяются по сохранённым настройкам */
const CONSENT_FIELDS: Array<{ key: keyof OrganizationSettings; label: string }> = [
  { key: 'organization_full_name', label: 'полное наименование' },
  { key: 'organization_inn', label: 'ИНН' },
  { key: 'organization_ogrn', label: 'ОГРН' },
  { key: 'organization_legal_address', label: 'юридический адрес' },
  { key: 'organization_email', label: 'электронная почта' },
]

const consentMissingFields = computed(() => {
  const settings = organizationSettingsStore.settings

  return CONSENT_FIELDS
    .filter(field => !String(settings?.[field.key] ?? '').trim())
    .map(field => field.label)
})

const pdConsentItems = computed<IExistingFile[]>(() => pdConsentUrl.value
  ? [{ id: 1, file: pdConsentUrl.value, filename: 'Согласие на обработку ПД.pdf' }]
  : [])

function applyPdDocuments(settings: OrganizationSettings) {
  pdAgreementExistingUrl.value = toMediaUrl(settings.pd_agreement_file)
  pdAgreementSynced.value = Boolean(settings.pd_agreement_synced)
  pdConsentUrl.value = toMediaUrl(settings.pd_consent_file)
  pdConsentSynced.value = Boolean(settings.pd_consent_synced)
}

const pdAgreementExistingItems = computed<IExistingFile[]>(() => {
  if (!pdAgreementExistingUrl.value || pdAgreementFiles.value.length > 0) return []
  let filename = 'Соглашение об обработке ПД.pdf'
  try {
    const clean = pdAgreementExistingUrl.value.split('?')[0]
    filename = decodeURIComponent(clean.substring(clean.lastIndexOf('/') + 1)) || filename
  } catch {}
  return [
    {
      id: 1,
      file: pdAgreementExistingUrl.value,
      filename,
    },
  ]
})
const form = reactive({
  organization_full_name: '',
  organization_short_name: '',
  organization_inn: '',
  organization_kpp: '',
  organization_ogrn: '',
  organization_legal_address: '',
  organization_phone: '',
  organization_email: '',
  organization_website: '',
})

function isActiveTab(value: SettingsTab) {
  return tab.value === value
}

function isLogoFile(file: File) {
  return LOGO_TYPES.includes(file.type) || LOGO_NAME.test(file.name)
}

function revokePreview() {
  if (logoPreview.value.startsWith('blob:')) {
    URL.revokeObjectURL(logoPreview.value)
  }
}

function applyLogoFile(file: File | null | undefined) {
  if (!file) {
    return
  }
  if (!isLogoFile(file)) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Загрузите изображение PNG, JPEG или WebP',
    })
    return
  }
  revokePreview()
  logoFile.value = file
  logoCleared.value = false
  logoPreview.value = URL.createObjectURL(file)
}

async function clearLogo(event?: MouseEvent) {
  event?.stopPropagation()
  if (logoFile.value) {
    revokePreview()
    logoFile.value = null
    const existingServerLogo = toMediaUrl(organizationSettingsStore.settings?.organization_logo)
    logoPreview.value = existingServerLogo || ''
    logoCleared.value = !existingServerLogo
    return
  }

  if (organizationSettingsStore.settings?.organization_logo) {
    deletingLogo.value = true
    try {
      const updated = await organizationSettingsStore.deleteLogo()
      revokePreview()
      logoFile.value = null
      logoPreview.value = toMediaUrl(updated?.organization_logo) || ''
      logoCleared.value = false
      feedbackStore.showToast({
        type: 'success',
        message: 'Логотип удален',
      })
    } catch (caught) {
      const message = getApiError(caught, 'Не удалось удалить логотип')
      feedbackStore.showToast({
        type: 'error',
        message,
      })
    } finally {
      deletingLogo.value = false
    }
    return
  }

  revokePreview()
  logoFile.value = null
  logoPreview.value = ''
  logoCleared.value = true
}

function openLogoPicker() {
  logoInputRef.value?.click()
}

function onLogoInputChange(event: Event) {
  const target = event.target as HTMLInputElement
  applyLogoFile(target.files?.[0])
  target.value = ''
}

function openPdAgreementPicker() {
  pdAgreementAttachRef.value?.openDialog()
}

async function onPdAgreementFilesUpdate(files: File[]) {
  if (!files.length) {
    pdAgreementFiles.value = []
    return
  }
  const latest = files[files.length - 1]
  if (latest.type !== 'application/pdf' && !/\.pdf$/i.test(latest.name)) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Соглашение об обработке ПД должно быть в формате PDF',
    })
    pdAgreementFiles.value = []
    return
  }

  pdAgreementFiles.value = [latest]
  uploadingPdAgreement.value = true
  try {
    const payload = new FormData()
    payload.append('pd_agreement_file', latest)
    const settings = await organizationSettingsStore.save(payload)
    applyPdDocuments(settings)
    feedbackStore.showToast(pdAgreementSynced.value
      ? { type: 'success', message: 'Соглашение об обработке ПД загружено и синхронизировано с ЛК пациента' }
      : { type: 'warning', message: 'Соглашение загружено, но не синхронизировано с ЛК пациента. Модуль обращений остаётся заблокированным' })
  } catch (caught) {
    feedbackStore.showToast({
      type: 'error',
      message: getApiError(caught, 'Не удалось загрузить соглашение об обработке ПД'),
    })
  } finally {
    pdAgreementFiles.value = []
    uploadingPdAgreement.value = false
  }
}

// Удаление соглашения блокирует модуль обращений для всей больницы — только после подтверждения
async function onRemovePdAgreementConfirm(confirmed: boolean) {
  showPdAgreementDeleteConfirm.value = false
  if (!confirmed || !pdAgreementExistingUrl.value) return
  uploadingPdAgreement.value = true
  try {
    const settings = await organizationSettingsStore.save({ pd_agreement_file: null })
    applyPdDocuments(settings)
    feedbackStore.showToast({
      type: 'success',
      message: 'Соглашение об обработке ПД удалено',
    })
  } catch (caught) {
    feedbackStore.showToast({
      type: 'error',
      message: getApiError(caught, 'Не удалось удалить соглашение об обработке ПД'),
    })
  } finally {
    uploadingPdAgreement.value = false
  }
}

function onDragEnter() {
  dragCounter.value += 1
  isDragOver.value = true
}

function onDragLeave() {
  dragCounter.value = Math.max(0, dragCounter.value - 1)
  if (dragCounter.value === 0) {
    isDragOver.value = false
  }
}

function onDrop(event: DragEvent) {
  dragCounter.value = 0
  isDragOver.value = false
  applyLogoFile(event.dataTransfer?.files?.[0])
}

async function load() {
  if (!canEditOrganization.value) {
    return
  }

  loading.value = true
  error.value = ''
  try {
    const settings = await organizationSettingsStore.load(true)
    if (settings) {
      Object.keys(form).forEach(key => {
        (form as Record<string, string>)[key] = String((settings as unknown as Record<string, unknown>)[key] || '')
      })
      logoPreview.value = toMediaUrl(settings.organization_logo)
      logoCleared.value = false
      applyPdDocuments(settings)
      pdAgreementFiles.value = []
    }
  } catch (caught) {
    error.value = getApiError(caught, 'Не удалось загрузить настройки организации')
  } finally {
    loading.value = false
  }
}

async function save() {
  saving.value = true
  error.value = ''
  try {
    const payload = new FormData()
    Object.entries(form).forEach(([key, value]) => payload.append(key, value))
    if (logoFile.value) {
      payload.append('organization_logo', logoFile.value)
    } else if (logoCleared.value) {
      payload.append('clear_organization_logo', 'true')
    }
    const settings = await organizationSettingsStore.save(payload)
    const nextPreview = toMediaUrl(settings.organization_logo)
    revokePreview()
    logoFile.value = null
    logoCleared.value = false
    logoPreview.value = nextPreview
    applyPdDocuments(settings)
    feedbackStore.showToast({
      type: 'success',
      message: 'Настройки организации сохранены',
    })
  } catch (caught) {
    const message = getApiError(caught, 'Не удалось сохранить настройки организации')
    error.value = message
    feedbackStore.showToast({
      type: 'error',
      message,
    })
  } finally {
    saving.value = false
  }
}

const onboardingStore = useOnboardingStore()

// Вкладка, открытая до онбординга: шаги переключают вкладки, после выхода возвращаем как было
const tabBeforeOnboarding = ref<SettingsTab | null>(null)

function showTabForOnboarding(value: SettingsTab) {
  tabBeforeOnboarding.value ??= tab.value
  tab.value = value
}

useOnboardingContext({
  names: [SETTINGS_CONTEXTS.page],
  scenarioId: 'settings',
  root: () => document.body,
  actions: {
    showOrganizationTab: () => showTabForOnboarding('organization'),
    showStructureTab: () => showTabForOnboarding('structure'),
    restoreTab: () => {
      if (tabBeforeOnboarding.value)
        tab.value = tabBeforeOnboarding.value
      tabBeforeOnboarding.value = null
    },
  },
})

onMounted(async () => {
  await load()
  void onboardingStore.autoStart('settings')
})

onBeforeUnmount(revokePreview)
</script>

<template>
  <div>
    <div
      :class="[
        smAndDown
          ? 'd-flex flex-column gap-2 mb-4 w-100'
          : 'd-flex flex-wrap align-center justify-space-between gap-4 mb-6',
      ]"
    >
      <div class="d-flex align-center ga-2 flex-wrap">
        <VBtn
          v-if="isActiveTab('organization') && canEditOrganization && !smAndDown"
          color="primary"
          prepend-icon="ri-save-line"
          :loading="saving"
          :data-onboarding="SETTINGS_ANCHORS.toolbar.save"
          @click="save"
        >
          Сохранить
        </VBtn>
        <VMenu
          v-if="isActiveTab('organization') && canImportNorms"
          location="bottom end"
          content-class="entity-form-menu"
        >
          <template #activator="{ props: menuProps }">
            <VBtn
              v-bind="menuProps"
              variant="outlined"
              color="secondary"
              prepend-icon="ri-download-2-line"
              append-icon="ri-arrow-down-s-line"
              :data-onboarding="SETTINGS_ANCHORS.toolbar.importNorms"
            >
              Импорт нормативных документов
            </VBtn>
          </template>
          <VList density="compact" min-width="220">
            <VListItem
              title="Начать импорт"
              @click="openQualityImport"
            />
            <VListItem
              title="История импорта"
              @click="openQualityHistory"
            />
          </VList>
        </VMenu>
        <VMenu
          v-if="isActiveTab('structure') && canEditStructure"
          location="bottom end"
          content-class="entity-form-menu"
        >
          <template #activator="{ props: menuProps }">
            <VBtn
              v-bind="menuProps"
              variant="outlined"
              color="secondary"
              prepend-icon="ri-download-2-line"
              append-icon="ri-arrow-down-s-line"
              :data-onboarding="SETTINGS_ANCHORS.toolbar.importLocations"
            >
              Импорт помещений
            </VBtn>
          </template>
          <VList density="compact" min-width="220">
            <VListItem
              title="Начать импорт"
              @click="openLocationsImport"
            />
            <VListItem
              title="История импорта"
              @click="openLocationsHistory"
            />
          </VList>
        </VMenu>
      </div>
      <div
        class="d-flex gap-2"
        :class="smAndDown ? 'flex-column w-100' : 'align-center ml-auto'"
      >
        <VBtn
          v-if="canEditOrganization"
          :active="isActiveTab('organization')"
          variant="outlined"
          color="secondary"
          :class="{ 'w-100': smAndDown }"
          prepend-icon="ri-hospital-line"
          :data-onboarding="SETTINGS_ANCHORS.tabs.organization"
          @click="tab = 'organization'"
        >
          Организация
        </VBtn>
        <VBtn
          v-if="canEditStructure"
          :active="isActiveTab('structure')"
          variant="outlined"
          color="secondary"
          :class="{ 'w-100': smAndDown }"
          prepend-icon="ri-building-4-line"
          :data-onboarding="SETTINGS_ANCHORS.tabs.structure"
          @click="tab = 'structure'"
        >
          Структура больницы
        </VBtn>
        <VBtn
          v-if="canEditOrganization"
          :active="isActiveTab('branding')"
          variant="outlined"
          color="secondary"
          :class="{ 'w-100': smAndDown }"
          prepend-icon="ri-palette-line"
          :data-onboarding="SETTINGS_ANCHORS.tabs.branding"
          @click="tab = 'branding'"
        >
          Фирменное оформление
        </VBtn>
        <VBtn
          v-if="canManageIntegrations"
          :active="isActiveTab('integrations')"
          variant="outlined"
          color="secondary"
          :class="{ 'w-100': smAndDown }"
          prepend-icon="ri-links-line"
          :data-onboarding="SETTINGS_ANCHORS.tabs.integrations"
          @click="tab = 'integrations'"
        >
          Интеграции
        </VBtn>
        <OnboardingLaunchButton scenario="settings" />
      </div>
    </div>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ error }}
    </VAlert>

    <VCard class="table-card border rounded pa-0">
      <VProgressLinear
        v-if="loading"
        indeterminate
        color="primary"
      />

      <VWindow v-model="tab" :touch="false">
        <VWindowItem v-if="canEditOrganization" value="organization">
          <div class="pa-6" :class="{ 'opacity-50': loading }">
            <div class="text-h6 mb-4">
              Основные сведения
            </div>
            <VRow>
              <VCol
                cols="12"
                md="8"
                :data-onboarding="SETTINGS_ANCHORS.organization.requisitesFirst"
              >
                <VTextField
                  v-model="form.organization_full_name"
                  label="Полное наименование организации"
                />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="form.organization_short_name"
                  label="Краткое наименование"
                />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="form.organization_inn"
                  label="ИНН"
                  maxlength="12"
                />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="form.organization_kpp"
                  label="КПП"
                  maxlength="9"
                />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="form.organization_ogrn"
                  label="ОГРН"
                  maxlength="15"
                />
              </VCol>
              <VCol cols="12" :data-onboarding="SETTINGS_ANCHORS.organization.requisitesLast">
                <VTextarea
                  v-model="form.organization_legal_address"
                  label="Юридический адрес"
                  rows="2"
                />
              </VCol>
            </VRow>

            <VDivider class="my-6" />

            <div class="text-h6 mb-4">
              Контакты
            </div>
            <VRow>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="form.organization_phone"
                  label="Телефон"
                />
              </VCol>
              <VCol
                cols="12"
                md="4"
                :data-onboarding="SETTINGS_ANCHORS.organization.email"
              >
                <VTextField
                  v-model="form.organization_email"
                  label="Электронная почта"
                  type="email"
                />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="form.organization_website"
                  label="Сайт"
                  type="url"
                />
              </VCol>
            </VRow>

            <VDivider class="my-6" />

            <div class="text-h6 mb-4">
              Логотип
            </div>
            <div
              class="logo-dropzone"
              :class="{
                'logo-dropzone--active': isDragOver,
                'logo-dropzone--filled': logoPreview,
              }"
              role="button"
              tabindex="0"
              @click="openLogoPicker"
              @keydown.enter.prevent="openLogoPicker"
              @keydown.space.prevent="openLogoPicker"
              @dragenter.prevent="onDragEnter"
              @dragover.prevent
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onDrop"
            >
              <VIconBtn
                v-if="logoPreview"
                class="logo-dropzone__remove"
                icon="ri-close-line"
                variant="text"
                size="32"
                icon-size="20"
                aria-label="Удалить логотип"
                :loading="deletingLogo"
                @click.stop="clearLogo"
              />
              <VImg
                v-if="logoPreview"
                :src="logoPreview"
                class="logo-dropzone__img"
                contain
              />
              <div v-else class="logo-dropzone__empty">
                <VIcon
                  icon="ri-upload-cloud-2-line"
                  size="36"
                  color="secondary"
                />
                <span class="text-body-2 text-medium-emphasis">
                  Можно перетащить файл в область превью или выбрать кнопкой. Рекомендуется PNG или JPEG на прозрачном или белом фоне.
                </span>
                <VBtn
                  variant="outlined"
                  color="secondary"
                  class="bg-surface"
                  prepend-icon="ri-upload-2-line"
                  @click.stop="openLogoPicker"
                >
                  Выбрать файл
                </VBtn>
              </div>
            </div>
            <div v-if="logoPreview" class="d-flex align-center ga-2 mt-3">
              <VBtn
                variant="outlined"
                color="secondary"
                size="small"
                prepend-icon="ri-upload-2-line"
                @click.stop="openLogoPicker"
              >
                Заменить файл
              </VBtn>
              <VBtn
                variant="text"
                color="error"
                size="small"
                prepend-icon="ri-delete-bin-line"
                :loading="deletingLogo"
                @click.stop="clearLogo"
              >
                Удалить логотип
              </VBtn>
            </div>
            <input
              ref="logoInputRef"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              class="d-none"
              @change="onLogoInputChange"
            >

            <CustomDivider
              title="Документы"
              class="my-6"
              :data-onboarding="SETTINGS_ANCHORS.organization.documentsFirst"
            />

            <!-- Оба документа нужны «Обращениям граждан»: согласие портал собирает из реквизитов, соглашение организация загружает сама -->
            <div class="text-body-2 text-medium-emphasis mb-4">
              Модуль «Обращения граждан» и публичная форма в личном кабинете пациента работают, когда оба документа готовы и синхронизированы.
            </div>

            <div :data-onboarding="SETTINGS_ANCHORS.organization.consent" class="mb-6">
              <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
                <div class="text-h6">
                  Согласие на обработку персональных данных
                </div>
                <VChip
                  v-if="pdConsentUrl && pdConsentSynced"
                  size="small"
                  color="success"
                  variant="tonal"
                  prepend-icon="ri-checkbox-circle-line"
                >
                  Синхронизировано с ЛК пациента
                </VChip>
                <VChip
                  v-else-if="pdConsentUrl"
                  size="small"
                  color="warning"
                  variant="tonal"
                  prepend-icon="ri-error-warning-line"
                >
                  Не синхронизировано с ЛК
                </VChip>
                <VChip
                  v-else
                  size="small"
                  color="secondary"
                  variant="tonal"
                >
                  Не сформировано
                </VChip>
              </div>

              <div class="text-body-2 text-medium-emphasis mb-2">
                Формируется автоматически из полного наименования, ИНН, ОГРН, юридического адреса и электронной почты после сохранения настроек и обновляется при их изменении.
              </div>
              <div
                v-if="consentMissingFields.length"
                class="text-body-2 text-warning mb-2"
              >
                Не заполнено: {{ consentMissingFields.join(', ') }}.
              </div>

              <FileList
                v-if="pdConsentItems.length"
                :items="pdConsentItems"
                :show-delete="false"
              />
            </div>

            <div
              class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2"
              :data-onboarding="SETTINGS_ANCHORS.organization.agreementFirst"
            >
              <div class="text-h6">
                Соглашение об обработке персональных данных
              </div>
              <div class="d-flex align-center ga-2">
                <VChip
                  v-if="uploadingPdAgreement"
                  size="small"
                  color="info"
                  variant="tonal"
                  prepend-icon="ri-loader-4-line"
                >
                  Синхронизация с ЛК...
                </VChip>
                <VChip
                  v-else-if="pdAgreementExistingUrl && pdAgreementSynced"
                  size="small"
                  color="success"
                  variant="tonal"
                  prepend-icon="ri-checkbox-circle-line"
                >
                  Синхронизировано с ЛК пациента
                </VChip>
                <VChip
                  v-else-if="pdAgreementExistingUrl"
                  size="small"
                  color="warning"
                  variant="tonal"
                  prepend-icon="ri-error-warning-line"
                >
                  Не синхронизировано с ЛК
                </VChip>
                <VChip
                  v-else
                  size="small"
                  color="secondary"
                  variant="tonal"
                >
                  Файл не загружен
                </VChip>
              </div>
            </div>

            <div class="text-body-2 text-medium-emphasis mb-4">
              PDF-документ вашей организации. Прикреплённый файл сразу сохраняется и передаётся в личный кабинет пациента.
            </div>

            <div class="d-flex flex-column ga-2" :data-onboarding="SETTINGS_ANCHORS.organization.agreementLast">
              <div class="d-flex align-center ga-4 flex-wrap">
                <VBtn
                  :class="smAndDown ? 'w-100' : 'align-self-start'"
                  variant="outlined"
                  color="secondary"
                  prepend-icon="ri-attachment-line"
                  :loading="uploadingPdAgreement"
                  @click="openPdAgreementPicker"
                >
                  {{ pdAgreementExistingUrl ? 'Заменить файл' : 'Прикрепить файл' }}
                </VBtn>
                <span
                  v-if="!smAndDown"
                  class="text-body-2 text-medium-emphasis"
                >или вставьте файл из буфера (Ctrl+V)</span>
              </div>

              <FileAttach
                ref="pdAgreementAttachRef"
                :files="pdAgreementFiles"
                :disabled="uploadingPdAgreement"
                accept="application/pdf,.pdf"
                paste-requires-focus
                @update:files="onPdAgreementFilesUpdate"
              />

              <FileList
                v-if="pdAgreementExistingItems.length"
                :items="pdAgreementExistingItems"
                :disabled="uploadingPdAgreement"
                :show-delete="true"
                @remove="showPdAgreementDeleteConfirm = true"
              />
            </div>

            <VBtn
              v-if="smAndDown"
              color="primary"
              prepend-icon="ri-save-line"
              class="mt-4 w-100"
              :loading="saving"
              :data-onboarding="SETTINGS_ANCHORS.toolbar.save"
              @click="save"
            >
              Сохранить
            </VBtn>
          </div>
        </VWindowItem>

        <VWindowItem v-if="canEditStructure" value="structure">
          <HospitalStructurePanel />
        </VWindowItem>
        <VWindowItem v-if="canEditOrganization" value="branding">
          <ReportBrandingPanel :active="tab === 'branding'" @organization="tab = 'organization'" />
        </VWindowItem>
        <VWindowItem v-if="canManageIntegrations" value="integrations">
          <WebhooksPanel :active="tab === 'integrations'" />
        </VWindowItem>
      </VWindow>
    </VCard>
    <ImportHistoryModal
      v-model="historyOpen"
      :import-type="historyImportType"
    />
    <ConfirmDialog
      v-model:is-dialog-visible="showPdAgreementDeleteConfirm"
      confirmation-question="Удалить соглашение об обработке ПД?"
      confirmation-text="Модуль «Обращения граждан» будет заблокирован, пока не загрузят новое соглашение."
      confirm-btn="Удалить"
      reject-btn="Отмена"
      @confirm="onRemovePdAgreementConfirm"
    />
  </div>
</template>

<style scoped lang="scss">
.table-card {
  border-color: rgba(var(--v-theme-on-surface), 0.08) !important;
}

.logo-dropzone {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;
  min-height: 220px;
  padding: 24px 32px;
  border: 1px dashed rgba(var(--v-theme-on-surface), 0.22);
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:focus-visible,
  &:hover,
  &--active {
    border-color: rgba(var(--v-theme-primary), 0.45);
    background: rgba(var(--v-theme-primary), 0.08);
  }

  &--filled {
    min-height: 220px;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;
  }

  @media (max-width: 959px) {
    width: 100%;
  }

  &__img {
    width: 100%;
    max-height: 180px;
  }

  &__remove {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;

    :deep(.v-icon) {
      color: rgb(var(--v-theme-secondary));
      transition: color 0.2s ease;
    }

    &:hover :deep(.v-icon) {
      color: rgb(var(--v-theme-error)) !important;
    }
  }
}
</style>
